import { action, makeObservable, observable, runInAction } from 'mobx';
import {
  StateMachineResponse,
  StateType,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { RootStore } from './root';
import type { DeviceTokenResponse } from './api';
import {
  DEVICE_AUTH_CLIENT_ID,
  DEVICE_AUTH_DEFAULT_INTERVAL,
  DEVICE_AUTH_SCOPE,
  DEVICE_AUTH_TOKEN_ERRORS,
} from '../utils/constants';
import { extractServerErrorCode } from '../utils/errorHandler';

const GENERIC_ERROR_KEY = 'device.authorize.error.generic';

export type DeviceAuthorizeStatus =
  | 'idle'
  | 'loading'
  | 'pending'
  | 'success'
  | 'error';

const AUTH_METHOD = 'device_authorization';
const REQUEST_DEVICE_CODE_INTERACTION = 'device-authorize-request-code';
// `unknown_error` is a transient server-side hiccup, not a device-code-state
// error. Retry the poll a few times before treating it as persistent and
// restarting the flow with a fresh code.
const MAX_UNKNOWN_ERROR_RETRIES = 3;

/**
 * RFC 8628 OAuth 2.0 Device Authorization Grant — the device (client) side.
 * Creates the authorization transaction (device + user code), exposes the QR
 * value, and polls the token endpoint until the user approves on a second
 * device. On success it hands off to the standard post-login funnel.
 */
export class DeviceAuthorizeStore {
  private readonly rootStore: RootStore;

  public status: DeviceAuthorizeStatus = 'idle';
  public userCode = '';
  public verificationUri = '';
  /** `verification_uri_complete` — the value encoded into the QR code. */
  public qrValue = '';
  /** Localized error message shown in the `error` state. */
  public errorMessage = '';

  private deviceCode = '';
  private intervalSeconds = DEVICE_AUTH_DEFAULT_INTERVAL;
  private unknownErrorRetries = 0;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  // Incremented on every reset() and dispose(). Each async operation captures
  // its generation before the first await and bails out if the value changed —
  // this prevents a stale continuation from a previous start() (e.g. React
  // Strict Mode double-invocation) from racing against the current one.
  private generation = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      status: observable,
      userCode: observable,
      verificationUri: observable,
      qrValue: observable,
      errorMessage: observable,
      start: action.bound,
      reset: action.bound,
      dispose: action.bound,
    });
  }

  public enableAccess(): boolean {
    // The device flow is intentionally public — the device is not authenticated;
    // approval happens on a second, already-authenticated device.
    return true;
  }

  /** Step 1: create the authorization transaction and begin polling. */
  public async start(): Promise<void> {
    this.reset();
    const gen = this.generation;
    this.status = 'loading';
    this.rootStore.fedopsLogger.interactionStarted(
      REQUEST_DEVICE_CODE_INTERACTION,
    );
    try {
      const response = await this.rootStore.apiStore.requestDeviceCode(
        DEVICE_AUTH_CLIENT_ID,
        DEVICE_AUTH_SCOPE,
      );
      if (gen !== this.generation) {
        this.rootStore.fedopsLogger.interactionEnded(
          REQUEST_DEVICE_CODE_INTERACTION,
        );
        return;
      }
      runInAction(() => {
        this.deviceCode = response.deviceCode ?? '';
        this.userCode = response.userCode ?? '';
        this.verificationUri = response.verificationUri ?? '';
        this.qrValue = this.buildVerificationUriComplete(
          response.verificationUri,
          response.userCode,
        );
        this.intervalSeconds =
          response.interval ?? DEVICE_AUTH_DEFAULT_INTERVAL;
        this.status = 'pending';
      });
      this.rootStore.fedopsLogger.interactionEnded(
        REQUEST_DEVICE_CODE_INTERACTION,
      );
      this.scheduleExpiry(response.expiresIn);
      this.schedulePoll();
    } catch (error) {
      this.rootStore.fedopsLogger.interactionEnded(
        REQUEST_DEVICE_CODE_INTERACTION,
      );
      if (gen !== this.generation) {
        return;
      }
      // Per the oauth2-ng device-flow contract, POST /oauth/device/authorize
      // exposes only two errors and neither is reachable from this client:
      // `invalid_client` (the QR-login client is allow-listed) and
      // `INVALID_ARGUMENT` (nonce > 512 chars — we send no nonce). There is no
      // throttle/rate-limit error on this endpoint, so any failure here is
      // genuinely unexpected and the generic error state is the right terminal.
      this.failWith();
    }
  }

  /** Reset to a clean state so the screen can request a fresh code. */
  public reset(): void {
    this.clearTimers();
    this.generation++;
    this.status = 'idle';
    this.userCode = '';
    this.verificationUri = '';
    this.qrValue = '';
    this.errorMessage = '';
    this.deviceCode = '';
    this.intervalSeconds = DEVICE_AUTH_DEFAULT_INTERVAL;
    this.unknownErrorRetries = 0;
  }

  /** Stop all timers — call on unmount to avoid leaking the polling loop. */
  public dispose(): void {
    this.generation++;
    this.clearTimers();
  }

  private buildVerificationUriComplete(uri = '', userCode = ''): string {
    if (!uri) {
      return '';
    }
    try {
      const url = new URL(uri);
      url.searchParams.set('user_code', userCode);
      return url.toString();
    } catch {
      const separator = uri.includes('?') ? '&' : '?';
      return `${uri}${separator}user_code=${encodeURIComponent(userCode)}`;
    }
  }

  private scheduleExpiry(expiresIn?: number): void {
    if (!expiresIn) {
      return;
    }
    // When the code lifetime elapses there's nothing for the user to recover —
    // just transparently request a fresh code + QR.
    this.expiryTimer = setTimeout(() => {
      this.start();
    }, expiresIn * 1000);
  }

  private schedulePoll(): void {
    this.pollTimer = setTimeout(this.poll, this.intervalSeconds * 1000);
  }

  /** Step 2: poll the token endpoint, honoring the RFC 8628 error contract. */
  private poll = async (): Promise<void> => {
    if (this.status !== 'pending') {
      return;
    }
    const gen = this.generation;
    try {
      const response = await this.rootStore.apiStore.pollDeviceToken(
        DEVICE_AUTH_CLIENT_ID,
        this.deviceCode,
      );
      if (gen !== this.generation) {
        return;
      }
      if (response.access_token) {
        await this.onAuthorized(response);
        return;
      }
      this.schedulePoll();
    } catch (error) {
      if (gen !== this.generation) {
        return;
      }
      this.handlePollError((error as DeviceTokenResponse)?.error);
    }
  };

  private handlePollError(errorCode?: string): void {
    switch (errorCode) {
      case DEVICE_AUTH_TOKEN_ERRORS.AUTHORIZATION_PENDING:
        // The normal "keep polling" signal — the user simply hasn't approved
        // yet. A clean pending response also clears any transient-error streak.
        this.unknownErrorRetries = 0;
        this.schedulePoll();
        break;
      case DEVICE_AUTH_TOKEN_ERRORS.UNKNOWN_ERROR:
        // Transient server-side failure while minting the token (e.g. the
        // account couldn't be resolved) — not our request and not the
        // device-code state. Retry the same poll; if it keeps failing, treat
        // the transaction as wedged and restart with a fresh code.
        if (this.unknownErrorRetries >= MAX_UNKNOWN_ERROR_RETRIES) {
          this.start();
        } else {
          this.unknownErrorRetries += 1;
          this.schedulePoll();
        }
        break;
      case DEVICE_AUTH_TOKEN_ERRORS.EXPIRED_TOKEN:
        // Terminal and overloaded — beyond true expiry this also covers an
        // unknown/invalid device_code and an already-redeemed code. Treat it as
        // "the code is dead": stop polling and transparently start over.
        this.start();
        break;
      case DEVICE_AUTH_TOKEN_ERRORS.INVALID_REQUEST:
      default:
        // `invalid_request` means a required parameter was missing (a client
        // bug, nothing the user can fix); any other error is likewise terminal.
        // Either way, stop polling and surface the generic error state.
        this.failWith();
    }
  }

  /**
   * Step 3: the token poll succeeded, but the /oauth/token response only proves
   * the device was approved — it doesn't mint a browser session. Exchange the
   * OIDC id_token for a real Wix session via IAM loginWithIdpConnectionTokenParams,
   * and only hand off to the post-login funnel once that exchange succeeds.
   */
  private async onAuthorized(response: DeviceTokenResponse): Promise<void> {
    this.clearTimers();
    const gen = this.generation;

    const idToken = response.id_token;
    if (!idToken) {
      // The device-code scope includes `openid`, so the token response is
      // contractually obliged to carry an id_token. Its absence means a
      // malformed response with nothing to exchange — terminal.
      this.failWith();
      return;
    }

    try {
      const loginResponse =
        await this.rootStore.apiStore.loginWithDeviceIdToken(idToken);
      if (gen !== this.generation) {
        return;
      }
      if (!this.isSuccessState(loginResponse)) {
        // The exchange resolved but didn't mint a session (e.g. an unexpected
        // MFA/require-factor state the device screen can't drive). Log the
        // state for diagnosis; the screen has only a generic terminal error.
        console.error(
          '[deviceAuthorize] id_token exchange returned non-success state',
          loginResponse.state,
        );
        this.failWith();
        return;
      }
      runInAction(() => {
        this.status = 'success';
      });
      // Reuse the standard post-login funnel (getFunnelRedirectUrl + enforcement
      // + redirect); the exchange above has now established a real browser
      // session for it to operate on.
      const additionalData = loginResponse.additionalData ?? {};
      this.rootStore.navigationStore.postLogin({
        uaToken: additionalData.uaToken?.strValue,
        authMethod: AUTH_METHOD,
      });
    } catch (error) {
      if (gen !== this.generation) {
        return;
      }
      // Every failure mode of the exchange is terminal from the device screen's
      // perspective — the connection being disabled, throttling, no email from
      // the IDP, an invalid/expired id_token, or a server error. None are
      // recoverable by requesting a fresh device code, so surface the generic
      // error state rather than restarting the transaction. The raw HTTP error
      // is already captured by api responseProxy; capture the resolved code here
      // so the failure isn't silently swallowed and stays diagnosable.
      const errorCode = extractServerErrorCode(error);
      console.error(
        '[deviceAuthorize] id_token exchange failed',
        errorCode,
        error,
      );
      this.failWith();
    }
  }

  /** Only SUCCESS/DONE means the session was minted and we can proceed. */
  private isSuccessState(response: StateMachineResponse): boolean {
    const stateValue = response.state as unknown as string;
    return (
      stateValue === StateType.SUCCESS ||
      stateValue === 'SUCCESS' ||
      stateValue === 'DONE'
    );
  }

  private failWith(): void {
    this.clearTimers();
    const message = this.rootStore.i18n.t(GENERIC_ERROR_KEY);
    runInAction(() => {
      this.status = 'error';
      this.errorMessage = message;
    });
  }

  private clearTimers(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }
}
